// ═══════════════════════════════════════════════════════════════
// BATCH SERVICE
// Path: src/services/batchService.js
//
// All Supabase operations for gift_batches table and
// batch-related gift operations.
//
// Blueprint: CMM_Batch_Gift_Entry_Blueprint_v1_0.md — Phase 2
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

export const batchService = {
  // ── Create a new batch ────────────────────────────────────────
  async createBatch(notes, batchDate, chapterId, userId) {
    try {
      // Generate batch number via RPC
      const { data: batchNumber, error: rpcError } = await supabase.rpc(
        "generate_batch_number"
      );
      if (rpcError) throw rpcError;

      const { data, error } = await supabase
        .from("gift_batches")
        .insert({
          chapter_id: chapterId,
          batch_number: batchNumber,
          batch_date: batchDate || new Date().toISOString().slice(0, 10),
          status: "open",
          notes: notes || null,
          created_by: userId,
        })
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("batchService.createBatch", {
        batchNumber: data.batch_number,
      });
      return data;
    } catch (error) {
      debugLog.error("batchService.createBatch", error);
      throw error;
    }
  },

  // ── Get batches ───────────────────────────────────────────────
  async getBatches(filters = {}) {
    try {
      let query = supabase
        .from("gift_batches")
        .select("*")
        .order("created_at", { ascending: false });

      if (filters.status) query = query.eq("status", filters.status);
      if (filters.limit) query = query.limit(filters.limit);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("batchService.getBatches", error);
      throw error;
    }
  },

  // ── Get single batch ──────────────────────────────────────────
  async getBatchById(batchId) {
    try {
      const { data, error } = await supabase
        .from("gift_batches")
        .select("*")
        .eq("id", batchId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      debugLog.error("batchService.getBatchById", error);
      throw error;
    }
  },

  // ── Update batch description (notes) ──────────────────────────
  // Admins may edit after close; managers may only edit while open.
  // Caller is responsible for gating the UI.
  async updateBatchNotes(batchId, notes) {
    try {
      const { data, error } = await supabase
        .from("gift_batches")
        .update({ notes: notes || null })
        .eq("id", batchId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("batchService.updateBatchNotes", { batchId });
      return data;
    } catch (error) {
      debugLog.error("batchService.updateBatchNotes", error);
      throw error;
    }
  },

  // ── Close batch ───────────────────────────────────────────────
  async closeBatch(batchId, userId) {
    try {
      // Recalculate totals from actual gifts
      const { data: gifts, error: giftsErr } = await supabase
        .from("gifts")
        .select("amount")
        .eq("batch_id", batchId);

      if (giftsErr) throw giftsErr;

      const giftCount = (gifts || []).length;
      const totalAmount = (gifts || []).reduce(
        (sum, g) => sum + (parseFloat(g.amount) || 0),
        0
      );

      const { data, error } = await supabase
        .from("gift_batches")
        .update({
          status: "closed",
          closed_at: new Date().toISOString(),
          closed_by: userId,
          gift_count: giftCount,
          total_amount: totalAmount,
        })
        .eq("id", batchId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("batchService.closeBatch", {
        batchId,
        giftCount,
        totalAmount,
      });
      return data;
    } catch (error) {
      debugLog.error("batchService.closeBatch", error);
      throw error;
    }
  },

  // ── Reopen batch (admin only) ─────────────────────────────────
  async reopenBatch(batchId) {
    try {
      const { data, error } = await supabase
        .from("gift_batches")
        .update({
          status: "open",
          closed_at: null,
          closed_by: null,
        })
        .eq("id", batchId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("batchService.reopenBatch", { batchId });
      return data;
    } catch (error) {
      debugLog.error("batchService.reopenBatch", error);
      throw error;
    }
  },

  // ── Delete batch (admin only) ─────────────────────────────────
  // Unlinks gifts (sets batch_id = NULL), then deletes batch row
  async deleteBatch(batchId) {
    try {
      // Unlink gifts first
      const { error: unlinkErr } = await supabase
        .from("gifts")
        .update({ batch_id: null })
        .eq("batch_id", batchId);

      if (unlinkErr) throw unlinkErr;

      // Delete batch
      const { error } = await supabase
        .from("gift_batches")
        .delete()
        .eq("id", batchId);

      if (error) throw error;
      debugLog.action("batchService.deleteBatch", { batchId });
    } catch (error) {
      debugLog.error("batchService.deleteBatch", error);
      throw error;
    }
  },

  // ── Get all gifts in a batch (for display + report) ───────────
  async getBatchGifts(batchId) {
    try {
      const { data, error } = await supabase
        .from("gifts")
        .select(
          "id, member_id, gift_type, amount, gift_date, fund_id, payment_method, check_number, notes, created_at, members (id, first_name, last_name, credentialed_full_name), funds (id, name, code)"
        )
        .eq("batch_id", batchId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      return (data || []).map((row) => ({
        id: row.id,
        memberId: row.member_id,
        memberName: row.members
          ? row.members.credentialed_full_name ||
            `${row.members.first_name} ${row.members.last_name}`.trim()
          : "Unknown",
        giftType: row.gift_type,
        amount: row.amount ? parseFloat(row.amount) : 0,
        giftDate: row.gift_date,
        fundId: row.fund_id,
        fundName: row.funds?.name || null,
        fundCode: row.funds?.code || null,
        paymentMethod: row.payment_method,
        checkNumber: row.check_number,
        notes: row.notes,
        createdAt: row.created_at,
      }));
    } catch (error) {
      debugLog.error("batchService.getBatchGifts", error);
      throw error;
    }
  },

  // ── Add a gift to a batch ─────────────────────────────────────
  async addGiftToBatch(batchId, giftData, userId) {
    try {
      const { data: rows, error } = await supabase
        .from("gifts")
        .insert({
          batch_id: batchId,
          member_id: giftData.memberId,
          gift_type: giftData.giftType || "cash",
          amount: giftData.amount,
          gift_date: giftData.giftDate,
          fund_id: giftData.fundId || null,
          payment_method: giftData.paymentMethod || null,
          check_number: giftData.checkNumber || null,
          notes: giftData.notes || null,
          created_by: userId,
        })
        .select("id");

      if (error) throw error;

      const giftId = rows && rows.length > 0 ? rows[0].id : null;
      debugLog.action("batchService.addGiftToBatch", {
        batchId,
        giftId,
        amount: giftData.amount,
      });
      return giftId;
    } catch (error) {
      debugLog.error("batchService.addGiftToBatch", error);
      throw error;
    }
  },

  // ── Remove a gift from a batch ────────────────────────────────
  async removeGiftFromBatch(giftId) {
    try {
      const { error } = await supabase.from("gifts").delete().eq("id", giftId);

      if (error) throw error;
      debugLog.action("batchService.removeGiftFromBatch", { giftId });
    } catch (error) {
      debugLog.error("batchService.removeGiftFromBatch", error);
      throw error;
    }
  },
};
